/**
 * useNextConversationId — Hook resolver
 *
 * Lê o cache ativo do React Query da query `['conversations', 'infinite', ...]`
 * (populada por `useInfiniteConversations` na sidebar) e retorna o ID da conversa
 * imediatamente ABAIXO de `currentId` na lista renderizada atualmente.
 *
 * - Se `currentId` for o último item: retorna o anterior.
 * - Se `currentId` for o único item / não estiver na lista / não houver query ativa: retorna `null`.
 *
 * Retorna uma função (não um valor) para que a leitura do cache aconteça no momento
 * do clique em "Finalizar" (estado-atual-no-clique), não num snapshot do render.
 */
import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';

interface ConversationLike {
  id: string;
}

interface InfinitePage {
  items: ConversationLike[];
}

interface InfiniteData {
  pages: InfinitePage[];
}

export const useNextConversationId = (): ((currentId: string) => string | null) => {
  const queryClient = useQueryClient();

  return useCallback(
    (currentId: string): string | null => {
      const activeQueries = queryClient.getQueriesData<InfiniteData>({
        queryKey: ['conversations', 'infinite'],
        type: 'active',
      });

      if (activeQueries.length === 0) return null;

      // Usa a primeira query ativa — a sidebar só mantém 1 por vez (por aba).
      const [, data] = activeQueries[0];
      if (!data || !Array.isArray(data.pages)) return null;

      const flatList = data.pages.flatMap((page) => page.items ?? []);
      const currentIndex = flatList.findIndex((item) => item.id === currentId);

      if (currentIndex === -1) return null;

      // Próximo item abaixo
      if (currentIndex + 1 < flatList.length) {
        return flatList[currentIndex + 1]?.id ?? null;
      }

      // Era o último — pega o anterior (se existir)
      if (currentIndex - 1 >= 0) {
        return flatList[currentIndex - 1]?.id ?? null;
      }

      // Única conversa da lista
      return null;
    },
    [queryClient]
  );
};
